// Tooltips and knowledge-base rendering (ERP-66, ERP-67).
import { t } from "@keel/core";
import { Popover } from "radix-ui";
import { Fragment, type ReactNode, useRef, useState } from "react";
import { Link } from "react-router-dom";

/**
 * A small "?" that explains the thing beside it — a field, a column, a button. Opens on hover and
 * focus, and on tap where there is no hover. Rendered in place rather than portalled, so it works
 * inside a modal dialog's top layer.
 */
export function Tip({ children, label }: { children: ReactNode; label?: string }) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hover = (v: boolean) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(v), v ? 120 : 160);
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="tip-btn"
          aria-label={label ?? t("What is this?")}
          onPointerEnter={(e) => e.pointerType === "mouse" && hover(true)}
          onPointerLeave={(e) => e.pointerType === "mouse" && hover(false)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          // A tip inside a <label> must not move focus to the field or toggle a checkbox.
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen((o) => !o);
          }}
        >
          ?
        </button>
      </Popover.Trigger>
      <Popover.Content
        className="tip-pop"
        side="top"
        align="start"
        sideOffset={6}
        collisionPadding={12}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerEnter={() => hover(true)}
        onPointerLeave={() => hover(false)}
      >
        {children}
        <Popover.Arrow className="tip-arrow" width={10} height={5} />
      </Popover.Content>
    </Popover.Root>
  );
}

/** A link to a knowledge-base article, e.g. `<HelpLink to="loans/terminations" />`. */
export const HelpLink = ({ to, children }: { to: string; children?: ReactNode }) => (
  <Link to={`/help/${to}`} className="help-link">
    <span className="tip-btn" aria-hidden="true">
      ?
    </span>
    {children ?? t("How this works")}
  </Link>
);

// ── Markdown ─────────────────────────────────────────────────────────────────────────────────────
// The subset articles and release notes use: headings, paragraphs, lists (one level of nesting),
// quotes as call-outs, tables, fenced code, and **bold**, *italic*, `code` and [links](/route).
// Rendered to React elements, never to HTML, so nothing in a file can inject markup.

function inline(text: string, key = 0): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\))/g;
  let last = 0;
  let k = key;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    k++;
    if (m[2] !== undefined) out.push(<strong key={k}>{inline(m[2], k * 100)}</strong>);
    else if (m[3] !== undefined) out.push(<em key={k}>{inline(m[3], k * 100)}</em>);
    else if (m[4] !== undefined) out.push(<code key={k}>{m[4]}</code>);
    else {
      const [label, href] = [m[5] ?? "", m[6] ?? ""];
      out.push(
        href.startsWith("/") ? (
          <Link key={k} to={href}>
            {inline(label, k * 100)}
          </Link>
        ) : /^https?:\/\//.test(href) ? (
          <a key={k} href={href} target="_blank" rel="noopener noreferrer">
            {inline(label, k * 100)}
          </a>
        ) : (
          label
        ),
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const cells = (row: string) =>
  row
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());

type Item = { text: string; sub: string[] };

export function Markdown({ source, className = "" }: { source: string; className?: string }) {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const at = (n: number) => lines[n] ?? "";
  const blocks: ReactNode[] = [];
  let i = 0;
  const isList = (l: string) => /^(\s*)([-*]|\d+\.)\s+/.test(l);
  const special = (l: string) =>
    /^#{1,4}\s/.test(l) || /^>/.test(l) || /^```/.test(l) || /^\|/.test(l) || isList(l) || /^---+$/.test(l);
  while (i < lines.length) {
    const line = at(i);
    const k = blocks.length;
    if (!line.trim()) {
      i++;
      continue;
    }
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const text = h[2] ?? "";
      const level = Math.min((h[1] ?? "").length + 1, 5);
      const H = `h${level}` as "h2";
      const id = text
        .toLowerCase()
        .replace(/[^a-z0-9ā-ž]+/g, "-")
        .replace(/^-|-$/g, "");
      blocks.push(
        <H key={k} id={id}>
          {inline(text)}
        </H>,
      );
      i++;
      continue;
    }
    if (/^---+$/.test(line)) {
      blocks.push(<hr key={k} />);
      i++;
      continue;
    }
    if (line.startsWith("```")) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !at(i).startsWith("```")) code.push(at(i++));
      i++;
      blocks.push(
        <pre key={k}>
          <code>{code.join("\n")}</code>
        </pre>,
      );
      continue;
    }
    if (line.startsWith(">")) {
      const q: string[] = [];
      while (i < lines.length && at(i).startsWith(">")) q.push(at(i++).replace(/^>\s?/, ""));
      const tone = q[0]?.match(/^\*\*(Note|Tip|Warning|Important)\*\*/i)?.[1]?.toLowerCase() ?? "note";
      blocks.push(
        <div key={k} className={`md-callout ${tone}`}>
          <Markdown source={q.join("\n")} />
        </div>,
      );
      continue;
    }
    if (line.startsWith("|")) {
      const rows: string[][] = [];
      while (i < lines.length && at(i).startsWith("|")) {
        if (!/^\|[\s:|-]+\|$/.test(at(i).trim())) rows.push(cells(at(i)));
        i++;
      }
      const [head = [], ...body] = rows;
      blocks.push(
        <div key={k} className="md-table">
          <table>
            <thead>
              <tr>
                {head.map((c, j) => (
                  <th key={j}>{inline(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((r, n) => (
                <tr key={n}>
                  {r.map((c, j) => (
                    <td key={j}>{inline(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    if (isList(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: Item[] = [];
      while (i < lines.length && (isList(at(i)) || /^\s{2,}\S/.test(at(i)))) {
        const l = at(i++);
        const m = l.match(/^(\s*)(?:[-*]|\d+\.)\s+(.*)$/);
        const last = items[items.length - 1];
        if (m && (m[1] ?? "").length < 2) items.push({ text: m[2] ?? "", sub: [] });
        else if (last) {
          if (m) last.sub.push(m[2] ?? "");
          // A wrapped line continues the item above it: the sub-item when there is one.
          else if (last.sub.length && /^\s{4,}\S/.test(l)) last.sub[last.sub.length - 1] += ` ${l.trim()}`;
          else last.text += ` ${l.trim()}`;
        }
      }
      const L = ordered ? "ol" : "ul";
      blocks.push(
        <L key={k}>
          {items.map((it, n) => (
            <li key={n}>
              {inline(it.text)}
              {it.sub.length > 0 && (
                <ul>
                  {it.sub.map((s, j) => (
                    <li key={j}>{inline(s)}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </L>,
      );
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && at(i).trim() && !special(at(i))) para.push(at(i++).trim());
    blocks.push(<p key={k}>{inline(para.join(" "))}</p>);
  }
  return (
    <div className={`md ${className}`}>
      {blocks.map((b, n) => (
        <Fragment key={n}>{b}</Fragment>
      ))}
    </div>
  );
}
